import { useMemo, useState, useCallback } from "react";
import {
  ExchangeVariant,
  ExtendedOrderItem,
  ExtendedReturnItemInput,
  ReturnReasonsDefaultType,
  ReturnReasonsReturnType,
  ReturnReasonsStepsProps,
  VariantInfo,
} from "./types";
import { AvailableReturnItemReason } from "@narvar/nth-kit-returns-headless";

export type ReturnReasonsFormDataValues = {
  type?: ReturnReasonsReturnType;
  selection: AvailableReturnItemReason[];
  quantity?: number;
  pictures?: File[];
  customerComments?: string;
  attested?: boolean;
  exchangeSelections?: VariantInfo;
  exchangeTo?: ExchangeVariant;
};

export type ReturnReasonsFormDataOptions = {
  orderItem: ExtendedOrderItem;
  selectedItem?: ExtendedReturnItemInput;
  stepsProps?: ReturnReasonsStepsProps;
  defaultType?: ReturnReasonsReturnType;
};

export type ReturnReasonsFormData = {
  values: ReturnReasonsFormDataValues;
  update: (
    values: Partial<ReturnReasonsFormDataValues>,
  ) => ReturnReasonsFormDataValues;
  reset: () => void;
};

export type SupportedTypes = {
  return: boolean;
  exchange: boolean;
  claim: boolean;
  defaultType?: ReturnReasonsDefaultType;
};

export const getSupportedTypes = ({
  orderItem,
  stepsProps,
}: {
  orderItem: ExtendedOrderItem;
  stepsProps?: ReturnReasonsStepsProps;
}): SupportedTypes => {
  const returnEnabled = !!orderItem.eligibilities.return?.isEligible;
  const exchangeEnabled =
    !!stepsProps?.exchanges?.enabled &&
    !!orderItem.eligibilities.exchange?.isEligible;
  const claimEnabled =
    !!orderItem.hasShippingProtection ||
    !!orderItem.eligibilities.claims?.isEligible;
  const noNonReturnTypes = !(exchangeEnabled || claimEnabled);
  return {
    return: returnEnabled,
    exchange: exchangeEnabled,
    claim: claimEnabled,
    defaultType: returnEnabled && noNonReturnTypes ? "return" : undefined,
  };
};

export const useReturnReasonsFormData: (
  options: ReturnReasonsFormDataOptions,
) => ReturnReasonsFormData = ({
  orderItem,
  selectedItem,
  stepsProps,
  defaultType,
}) => {
  defaultType =
    defaultType ?? getSupportedTypes({ orderItem, stepsProps }).defaultType;
  const initialValues: ReturnReasonsFormDataValues = useMemo(
    () => ({
      type: selectedItem?.exchangeTo
        ? "exchange"
        : selectedItem
          ? "return"
          : defaultType,
      selection: selectedItem
        ? ([
            orderItem.availableReasons?.find(
              (reason) => reason.id === selectedItem?.reason.id,
            ),
          ].filter((e) => e) as AvailableReturnItemReason[])
        : [],
      quantity:
        selectedItem?.quantity ??
        (orderItem.remainingQuantity === 1 ? 1 : undefined),
      pictures: selectedItem?.pictures,
      customerComments: selectedItem?.customerComments ?? "",
      attested: selectedItem?.attested ?? false,
      exchangeSelections: selectedItem?.exchangeTo?.variantInfo,
      exchangeTo: selectedItem?.exchangeTo,
    }),
    [selectedItem, orderItem, defaultType],
  );

  const [values, setValues] =
    useState<ReturnReasonsFormDataValues>(initialValues);

  const reset = useCallback(() => {
    setValues(initialValues);
  }, [initialValues]);

  const update: (
    updates: Partial<ReturnReasonsFormDataValues>,
  ) => ReturnReasonsFormDataValues = useCallback(
    (updates) => {
      const hasUpdate = Object.entries(updates).some(
        ([key, newValue]) =>
          values[key as keyof ReturnReasonsFormDataValues] !== newValue,
      );
      if (hasUpdate) {
        const newValues: ReturnReasonsFormDataValues = {
          ...values,
          ...updates,
        };
        setValues(newValues);
        return newValues;
      }
      return values;
    },
    [values],
  );

  return useMemo(() => ({ values, update, reset }), [values, update, reset]);
};
